import { useState, useRef, useEffect, useMemo, ChangeEvent, FormEvent } from 'react';
import cn from 'classnames';
import { connect, useDispatch } from 'react-redux';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import {
  captchaSetError,
  captchaSetIsActive,
  captchaLoad,
  captchaGetImage,
  captchaReset,
  captchaGetAudio,
} from '../actions/captchaActions';
import {
  getCaptchaIsFetching,
  getCaptchaError,
  getCaptchaIsActive,
  getCaptchaImage,
  getCaptchaAudio,
} from '../selectors/captchaSelectors';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import styles from './CaptchaPopup.module.scss';

interface ICaptchaPopupStoreProps {
  isTokenFetching: boolean;
  error: string;
  isActive: boolean;
  image: string;
  audio: string;
}

export const CaptchaPopupWithoutStore = ({
  isActive,
  isTokenFetching,
  error,
  image,
  audio,
}: ICaptchaPopupStoreProps) => {
  const dispatch = useDispatch();
  const [captchaInputValue, setCaptchaInputValue] = useState('');
  const [isPlayed, setIsPlayed] = useState(false);
  const sound = useMemo(() => new Audio(`data:audio/wav;base64,${audio}`), [audio]);
  const inputRef = useRef<HTMLInputElement>(null);

  const resetCaptcha = () => {
    sound.pause();
    dispatch(captchaReset());
    setCaptchaInputValue('');
    setIsPlayed(false);
  };

  useEffect(() => {
    if (isActive && !isTokenFetching) {
      dispatch(captchaGetImage());
    }
    if (!isActive) {
      resetCaptcha();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    if (error) {
      setCaptchaInputValue('');
    }
  }, [error]);

  const handleCaptchaRefreshButton = () => {
    resetCaptcha();
    dispatch(captchaGetImage());
    inputRef.current?.focus();
  };

  const handleCaptchaCloseButtonClick = () => {
    dispatch(captchaSetIsActive(false));
    setCaptchaInputValue('');
    dispatch(captchaSetError(''));
  };

  const handleCaptchaInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCaptchaInputValue(e.target.value);
  };

  const handleCaptchaSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (captchaInputValue && !isTokenFetching) {
      dispatch(captchaLoad(captchaInputValue));
    }
  };

  const handlePlayAudioCaptcha = () => {
    setIsPlayed(true);
    dispatch(captchaGetAudio());
  };

  useEffect(() => {
    if (sound && audio) {
      const fragment = document.createDocumentFragment();
      fragment.appendChild(sound);
      sound.addEventListener('ended', () => {
        setIsPlayed(false);
        fragment.removeChild(sound);
      });
      sound.play();
    }
  }, [sound, audio]);

  return (
    <>
      {isActive && (
        <div className={styles.CaptchaPopupBlock}>
          <form
            className={cn(styles.CaptchaPopup, isTokenFetching ? styles.CaptchaPopupFetching : '')}
            onSubmit={handleCaptchaSubmit}
          >
            <div className={styles.CaptchaImageBlock}>
              {image && <img className={styles.CaptchaImage} src={image} alt="captcha" />}
            </div>
            <button
              className={styles.CaptchaAudioButton}
              type="button"
              onClick={handlePlayAudioCaptcha}
              disabled={isPlayed}
            />
            <button
              className={styles.CaptchaRefreshButton}
              type="button"
              onClick={handleCaptchaRefreshButton}
              disabled={isTokenFetching}
            />
            <div className={styles.InputField}>
              <input
                className={cn('shared-input', `${error ? styles.CaptchaInputError : ''}`)}
                ref={inputRef}
                name="captcha"
                onChange={handleCaptchaInputChange}
                value={captchaInputValue}
                autoComplete="off"
              />
              {error && <div className="shared-error-message">{error}</div>}
            </div>
            <Button
              className={styles.CaptchaSubmitButton}
              id="captchaSubmitButton"
              color="primary"
              type="submit"
              disabled={captchaInputValue.length < 1}
              text="Отправить"
            />
            <button className={styles.CaptchaCloseButton} onClick={handleCaptchaCloseButtonClick} type="button" />
          </form>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  isTokenFetching: getCaptchaIsFetching(state),
  error: getCaptchaError(state),
  isActive: getCaptchaIsActive(state),
  image: getCaptchaImage(state),
  audio: getCaptchaAudio(state),
});

export const CaptchaPopup = connect(mapStateToProps)(CaptchaPopupWithoutStore);
